import React, { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { auth, db } from '../firebase';

export default function PendingProperties({ onReviewed }) {
  const [properties, setProperties] = useState([]);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!db || !auth?.currentUser) {
      setError('Sign in with an authorized admin account to review properties.');
      return undefined;
    }
    const pendingQuery = query(collection(db, 'properties'), where('status', '==', 'PENDING_REVIEW'));
    return onSnapshot(pendingQuery, (snapshot) => {
      setProperties(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      setError('');
    }, () => setError('Could not load pending properties. Check admin permissions and Firestore rules.'));
  }, []);

  const review = async (property, decision) => {
    setBusyId(property.id);
    setError('');
    setNotice('');
    const approved = decision === 'APPROVED';
    try {
      await updateDoc(doc(db, 'properties', property.id), {
        status: approved ? 'PUBLISHED' : 'REJECTED',
        moderationStatus: decision,
        moderationNote: approved ? null : 'Rejected by admin',
        publishedAt: approved ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      });
      setNotice(`${property.title || 'Property'} ${approved ? 'approved and published' : 'rejected'}.`);
      onReviewed?.();
    } catch (reviewError) {
      setError(reviewError?.code === 'permission-denied'
        ? 'Only an authorized admin can review listings.'
        : 'The property could not be updated. Please try again.');
    } finally {
      setBusyId('');
    }
  };

  return <div className="pending-properties-panel" aria-live="polite">
    {error && <p className="form-error" role="alert">{error}</p>}
    {notice && <p className="form-success" role="status">{notice}</p>}
    <div className="admin-submission-list">
      {properties.length ? properties.map((property) => <article key={property.id}>
        <div>
          <span className="demo-badge">PENDING REVIEW</span>
          <h3>{property.title || 'Untitled property'}</h3>
          <p>{[property.locality, property.city, property.state].filter(Boolean).join(', ')}</p>
          <p>{property.purpose === 'RENT' ? `Rent: ₹${Number(property.monthlyRent || 0).toLocaleString('en-IN')}/month` : `Price: ₹${Number(property.discountedPrice || property.price || 0).toLocaleString('en-IN')}`}</p>
          {property.description && <p>{property.description}</p>}
        </div>
        <div className="property-review-actions">
          <button className="primary-action" disabled={Boolean(busyId)} onClick={() => review(property, 'APPROVED')}>Approve</button>
          <button className="secondary-action" disabled={Boolean(busyId)} onClick={() => review(property, 'REJECTED')}>Reject</button>
        </div>
      </article>) : !error && <p className="muted-copy">No properties are waiting for review.</p>}
    </div>
  </div>;
}
